import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Breadcrumbs from "@/components/Breadcrumbs";
import JsonLd from "@/components/JsonLd";
import usePageMeta from "@/hooks/use-page-meta";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import * as Icons from "lucide-react";
import { CheckCircle2, ArrowRight, Phone, MessageCircle, MapPin } from "lucide-react";
import { useBooking } from "@/contexts/BookingContext";
import { SERVICES, PHONES, WHATSAPP_URL, LOCATIONS, BRAND } from "@/constants/content";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { open } = useBooking();
  const service = SERVICES.find((s) => s.slug === slug);
  const canonical = `https://${BRAND.domain}/services/${slug}`;

  usePageMeta({
    title: service?.metaTitle || `${service?.title} at Home — KinFlex`,
    description: service?.metaDescription || service?.short || "",
    canonical,
    ogImage: service?.hero,
    ogType: "article",
  });

  if (!service) return <Navigate to="/services" replace />;

  const Icon = Icons[service.icon] || Icons.Heart;
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: service.title,
      description: service.description || service.short,
      url: canonical,
      procedureType: "TherapeuticProcedure",
      bodyLocation: "At Home",
      preparation: service.intro,
      followup: "Family updates and doctor coordination as required.",
      howPerformed: service.howWeHelp,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (service.faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <JsonLd data={jsonLd} />

      {/* Hero */}
      <section className="pt-28 pb-10 md:pt-36 md:pb-16 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative">
          <Breadcrumbs items={[{ label: "Services", to: "/services" }, { label: service.title }]} />
          <div className="mt-6 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-5">
                <div className="h-6 w-6 rounded-md bg-gradient-to-br from-[#0A84FF] to-[#10B981] flex items-center justify-center text-white">
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Home Healthcare Service</span>
              </div>
              <h1 className="font-display font-bold text-slate-900 text-4xl md:text-5xl lg:text-[52px] tracking-tight leading-[1.05]">
                {service.title}
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">{service.short}</p>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button onClick={() => open(service.title)} className="rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-7 h-12 font-semibold shadow-lg shadow-[#0A84FF]/25" data-testid="service-book-btn">
                  Book Now <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <a href={`tel:${PHONES[0]}`} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-900 px-6 h-12 font-semibold shadow-sm">
                  <Phone className="h-4 w-4" style={{ color: '#EF4444' }} /> Call {PHONES[0]}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white px-6 h-12 font-semibold">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden ring-1 ring-slate-200 premium-shadow">
              <img src={service.hero} alt={`${service.title} at home in Delhi NCR`} className="w-full h-[380px] md:h-[460px] object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Overview + sticky CTA */}
      <section className="section bg-white">
        <div className="container-page grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-12">
            <div>
              <div className="eyebrow mb-3">Introduction</div>
              <h2 className="font-display font-bold text-slate-900 text-3xl md:text-4xl tracking-tight">
                Comprehensive {service.title.toLowerCase()} at home.
              </h2>
              <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">{service.intro}</p>
              {service.description && <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">{service.description}</p>}
            </div>

            {service.whoNeeds && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Who needs this service</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                  {service.whoNeeds.map((w) => (
                    <li key={w} className="flex items-start gap-2.5 rounded-xl border border-slate-200/70 bg-[#F8FAFC] p-3">
                      <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#10B981' }} />
                      <span className="text-sm text-slate-700">{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.benefits && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Benefits</h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {service.benefits.map((b) => (
                    <div key={b} className="rounded-xl border border-slate-200/70 p-4 bg-white hover:border-[#0A84FF]/30 transition-colors">
                      <div className="flex items-start gap-2.5">
                        <div className="h-6 w-6 rounded-md bg-[#10B981]/10 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="h-4 w-4" style={{ color: '#10B981' }} />
                        </div>
                        <span className="text-sm text-slate-700 leading-relaxed">{b}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {service.situations && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Symptoms & situations we handle</h2>
                <ul className="mt-4 space-y-2">
                  {service.situations.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#0A84FF] shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.howWeHelp && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">How our team helps</h2>
                <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">{service.howWeHelp}</p>
              </div>
            )}

            {service.process && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Our process</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {service.process.map((p) => (
                    <div key={p.step} className="rounded-2xl border border-slate-200/70 bg-white p-5">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#0A84FF] to-[#10B981] text-white font-display font-bold flex items-center justify-center">
                        {p.step}
                      </div>
                      <div className="mt-3 font-display font-semibold text-slate-900">{p.title}</div>
                      <div className="text-sm text-slate-600 mt-1">{p.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {service.whyChoose && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Why choose KinFlex</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                  {service.whyChoose.map((w) => (
                    <li key={w} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#0A84FF' }} /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.coverage && (
              <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-[#0A84FF]/5 to-white p-6">
                <div className="text-xs font-bold uppercase tracking-[0.16em] mb-2" style={{ color: '#0A84FF' }}>Service Coverage</div>
                <p className="text-slate-700 leading-relaxed">
                  <MapPin className="inline h-4 w-4 mr-1.5" style={{ color: '#0A84FF' }} /> {service.coverage}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {LOCATIONS.map((l) => (
                    <Link key={l.slug} to={`/${service.slug}-in-${l.slug}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:border-[#0A84FF]/40">
                      {service.title} in {l.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {service.faqs && (
              <div>
                <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl tracking-tight">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="mt-5 space-y-2.5" data-testid="service-faq">
                  {service.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`sfaq-${i}`} className="border border-slate-200 rounded-xl bg-white data-[state=open]:border-[#0A84FF]/40 px-4">
                      <AccordionTrigger className="text-left font-display font-semibold text-slate-900 hover:no-underline py-4">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-slate-600 pb-4">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Emergency CTA */}
            <div className="rounded-3xl p-6 md:p-8 text-white relative overflow-hidden" style={{ background: 'linear-gradient(120deg, #EF4444 0%, #DC2626 60%, #B91C1C 100%)' }}>
              <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white, transparent 40%)' }} />
              <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/85">24×7 Emergency</div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl leading-tight mt-1">
                    Need urgent {service.title.toLowerCase()}?
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  <a href={`tel:${PHONES[0]}`} className="rounded-full bg-white text-red-700 px-5 py-3 font-bold text-sm">
                    <Phone className="inline h-4 w-4 mr-1.5" /> Call {PHONES[0]}
                  </a>
                  <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="rounded-full bg-[#25D366] text-white px-5 py-3 font-bold text-sm">
                    <MessageCircle className="inline h-4 w-4 mr-1.5" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 self-start space-y-6">
            <div className="rounded-3xl bg-slate-900 text-white p-6 md:p-8">
              <div className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: '#4dabff' }}>Quick Booking</div>
              <h3 className="mt-3 font-display font-bold text-2xl leading-tight">Book {service.title} — same day.</h3>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">Speak with our care coordinator now.</p>
              <div className="mt-6 space-y-2.5">
                <Button onClick={() => open(service.title)} className="w-full rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white h-11 font-semibold">Book Appointment</Button>
                <a href={`tel:${PHONES[0]}`} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 h-11 font-semibold">
                  <Phone className="h-4 w-4" style={{ color: '#EF4444' }} /> Call {PHONES[0]}
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white h-11 font-semibold">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
            </div>

            {service.related && (
              <div className="rounded-2xl border border-slate-200/70 bg-white p-5">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Related Services</div>
                <ul className="space-y-1.5">
                  {service.related.map((r) => {
                    const rs = SERVICES.find((s) => s.slug === r);
                    return rs ? (
                      <li key={r}>
                        <Link to={`/services/${rs.slug}`} className="text-sm text-slate-700 hover:text-[#0A84FF]">→ {rs.title}</Link>
                      </li>
                    ) : null;
                  })}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Other services grid */}
      <section className="section bg-[#F8FAFC]">
        <div className="container-page">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display font-bold text-slate-900 text-2xl md:text-3xl">Other services</h2>
            <Link to="/services" className="text-sm font-semibold text-[#0A84FF]">All services →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {others.map((o) => {
              const OIcon = Icons[o.icon] || Icons.Heart;
              return (
                <Link key={o.slug} to={`/services/${o.slug}`} className="group rounded-2xl border border-slate-200/70 bg-white p-6 hover:border-[#0A84FF]/30 hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0A84FF]/10 to-[#10B981]/10 flex items-center justify-center mb-4">
                    <OIcon className="h-5 w-5" style={{ color: '#0A84FF' }} />
                  </div>
                  <div className="font-display font-semibold text-slate-900">{o.title}</div>
                  <div className="text-sm text-slate-600 mt-1 line-clamp-2">{o.short}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
}
